import 'iframe-resizer/js/iframeResizer.contentWindow';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { BlockEditor } from '@/editor/components/BlockEditor';
import { Icon } from '@/editor/components/ui/Icon';
import { Surface } from '@/editor/components/ui/Surface';
import { Toolbar } from '@/editor/components/ui/Toolbar';
import { useCollaboration } from '@/editor/hooks/useCollaboration';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';

import SnapshotService from 'Src/services/snapshot-service';
import { marked } from 'marked';

const useDarkmode = () => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setIsDarkMode(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  const toggleDarkMode = useCallback(
    () => setIsDarkMode((isDark) => !isDark),
    []
  );
  const lightMode = useCallback(() => setIsDarkMode(false), []);
  const darkMode = useCallback(() => setIsDarkMode(true), []);

  return {
    isDarkMode,
    toggleDarkMode,
    lightMode,
    darkMode,
  };
};

export default function Document() {
  const { isDarkMode, darkMode, lightMode } = useDarkmode();
  const [aiToken, setAiToken] = useState<string | null | undefined>();
  const [searchParams] = useSearchParams();
  const [text, setText] = useState<string | null | undefined>();
  const params = useParams();
  const providerState = useCollaboration({
    docId: params?.id,
    enabled: parseInt(searchParams.get('noCollab') ?? '0') !== 1,
  });

  const fetchSnapshotText = async () => {
    const response = await SnapshotService.text(params?.id);
    const markdown = response.data.text;
    const html_text = marked(markdown);
    setText(html_text);
    return;
  };

  useEffect(() => {
    // fetch dat

    fetchSnapshotText();
    const dataFetch = async () => {
      try {
        const response = await fetch('/api/ai', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(
            'No AI token provided, please set TIPTAP_AI_SECRET in your environment'
          );
        }
        const data = await response.json();

        const { token } = data;

        // set state when the data received
        setAiToken(token);
      } catch (e) {
        if (e instanceof Error) {
          console.error(e.message);
        }
        setAiToken(null);
        return;
      }
    };

    dataFetch();
  }, []);

  if (providerState.state === 'loading' || aiToken === undefined) return;

  const DarkModeSwitcher = createPortal(
    <Surface className="flex items-center gap-1 fixed bottom-6 right-6 z-[99999] p-1">
      <Toolbar.Button onClick={lightMode} active={!isDarkMode}>
        <Icon name="Sun" />
      </Toolbar.Button>
      <Toolbar.Button onClick={darkMode} active={isDarkMode}>
        <Icon name="Moon" />
      </Toolbar.Button>
    </Surface>,
    document.body
  );

  return (
    <main className="h-full overflow-y-auto">
      {' '}
      {DarkModeSwitcher}
      <BlockEditor
        aiToken={aiToken ?? undefined}
        ydoc={providerState.yDoc}
        provider={providerState.provider}
        text={text}
      />
    </main>
  );
}
